import React, { useState } from 'react';
import { Paperclip } from 'lucide-react';
import {
  messageTime, initials, firstName, shortName, contactRoleLabel, localPart, aliasInfo, formatBytes,
  isAutomaticMessage, avatarTone, splitQuotedText,
} from '../../lib/communications';

// The message list for one email conversation — shared by the Account →
// Communications tab (inside an expanded row) and the Needs-reply pane.
// Automatic replies (out-of-office, auto-acks) render dimmed with a pill and
// never decide anything: `needsReply` comes from the server.
//
// Long threads fold like Gmail: the first message and the last few stay
// open, everything in between collapses into one "N earlier messages" row.
// Quoted tails ("On … wrote:", ">" runs, Outlook headers) hide behind a
// "Show quoted text" toggle so a reply reads as just the new words.

export const THREAD_FOLD_ABOVE = 6;
const THREAD_TAIL_VISIBLE = 4;

export function Avatar({ label, className = '', size = 'md' }) {
  const dims = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-7 h-7 text-[11px]';
  return (
    <span aria-hidden="true" className={`inline-grid place-items-center rounded-full font-semibold shrink-0 ${dims} ${className}`}>
      {initials(label)}
    </span>
  );
}

export function Pill({ tone = 'grey', children, className = '' }) {
  const tones = {
    grey: 'border-white/[0.08] text-gray-400',
    amber: 'border-amber-500/35 bg-amber-500/[0.12] text-amber-300',
    lime: 'border-[#B0FC38]/35 bg-[#B0FC38]/[0.12] text-[#B0FC38]',
    sky: 'border-sky-400/35 bg-sky-400/[0.12] text-sky-300',
    red: 'border-red-500/35 bg-red-500/[0.12] text-red-300',
    muted: 'border-white/[0.06] text-gray-500 bg-white/[0.02]',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider whitespace-nowrap ${tones[tone] || tones.grey} ${className}`}>
      {children}
    </span>
  );
}

export function participantLabel(p) {
  const name = p.contactName || p.name;
  const role = p.contactRoleTitle || (p.contactRole ? contactRoleLabel(p.contactRole) : '');
  const base = name ? `${shortName(name, p.email)}` : p.email;
  return role ? `${base} · ${role}` : (name ? `${base} <${p.email}>` : base);
}

export default function ConversationThread({ messages = [], emptyText = 'No messages loaded for this conversation.', className = '' }) {
  const [showAll, setShowAll] = useState(false);
  const folded = !showAll && messages.length > THREAD_FOLD_ABOVE;
  const head = folded ? messages.slice(0, 1) : messages;
  const hidden = folded ? messages.slice(1, messages.length - THREAD_TAIL_VISIBLE) : [];
  const tail = folded ? messages.slice(messages.length - THREAD_TAIL_VISIBLE) : [];

  return (
    <div className={`space-y-3 ${className}`} data-testid="conversation-thread">
      {head.map((m) => <MessageCard key={m.id} message={m} />)}
      {hidden.length > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          aria-expanded={false}
          data-testid="thread-collapsed"
          className="w-full flex items-center gap-3 rounded-lg border border-dashed border-white/[0.1] bg-white/[0.02] px-3.5 py-2 text-[12px] text-gray-400 hover:text-white hover:border-white/[0.2] transition-colors"
        >
          <span aria-hidden="true" className="inline-grid place-items-center w-7 h-7 rounded-full bg-white/[0.05] text-[11px] font-semibold tabular-nums text-gray-300">{hidden.length}</span>
          <span>{hidden.length} earlier message{hidden.length === 1 ? '' : 's'}</span>
          <span className="ml-auto text-gray-500">Show all</span>
        </button>
      )}
      {tail.map((m) => <MessageCard key={m.id} message={m} />)}
      {messages.length === 0 && <div className="text-[12px] text-gray-500 py-2">{emptyText}</div>}
    </div>
  );
}

export function MessageCard({ message: m }) {
  const [showHtml, setShowHtml] = useState(false);
  const [showQuoted, setShowQuoted] = useState(false);
  const inbound = m.direction === 'inbound';
  const automatic = isAutomaticMessage(m);
  const to = (m.participants || []).filter((p) => p.kind === 'to');
  const cc = (m.participants || []).filter((p) => p.kind === 'cc');
  const fromP = (m.participants || []).find((p) => p.kind === 'from');
  const fromRole = fromP?.contactRoleTitle || (fromP?.contactRole ? contactRoleLabel(fromP.contactRole) : '');
  const fromName = m.from?.name || fromP?.contactName || m.from?.email;
  // Outbound mail shows the human behind a shared display name ("NexSav
  // Support · sent by Husain") — the person who clicked send, else the
  // mailbox owner.
  const sender = m.sentByName || m.staffName;
  const showSentBy = !inbound && sender && sender !== fromName;
  const alias = aliasInfo(m);
  const hasText = !!(m.bodyText && m.bodyText.trim());
  const hasHtml = !!(m.bodyHtml && m.bodyHtml.trim());
  const staffFirst = firstName(m.staffName);
  const { visible, quoted } = hasText ? splitQuotedText(m.bodyText) : { visible: '', quoted: '' };

  let sourceChip = null;
  if (automatic) sourceChip = <Pill tone="muted">Automatic reply</Pill>;
  else if (m.broadcast) sourceChip = <Pill tone="lime">Broadcast · {m.broadcast.name}</Pill>;
  else if (m.source === 'outreach') sourceChip = <Pill tone="lime">Sequence{m.outreachCampaign ? ` · ${m.outreachCampaign}` : ''}</Pill>;
  else if (m.source === 'notification') sourceChip = <Pill tone="lime">Portal notice</Pill>;
  else if (m.source === 'gmail' && !inbound) sourceChip = <Pill>Written in Gmail</Pill>;
  else if (inbound) sourceChip = <Pill tone="sky">From school</Pill>;

  return (
    <article
      className={`rounded-lg border border-white/[0.07] bg-white/[0.03] px-3.5 py-3 border-l-[3px] ${inbound ? (automatic ? 'border-l-sky-400/30' : 'border-l-sky-400') : 'border-l-white/20'} ${automatic ? 'opacity-70' : ''}`}
      data-testid={`message-${m.direction}`}
      data-automatic={automatic ? 'true' : undefined}
    >
      <div className="grid grid-cols-[28px_minmax(0,1fr)] gap-x-3">
        <Avatar label={fromName} className={`${avatarTone(m.from?.email || fromName)} mt-0.5`} />
        <div className="min-w-0">
          <header className="flex justify-between gap-3 flex-wrap text-[12px] text-gray-500">
            <span className="min-w-0">
              <b className="text-gray-100 font-semibold">{fromName}</b>
              {m.from?.email && m.from.email !== fromName && <span> &lt;{m.from.email}&gt;</span>}
              {fromRole && <span> · {fromRole}</span>}
              {showSentBy && <span> · sent by {firstName(sender)}</span>}
            </span>
            <time className="tabular-nums whitespace-nowrap ml-auto" dateTime={m.sentAt}>{messageTime(m.sentAt)}</time>
          </header>
          <div className="flex justify-between gap-3 flex-wrap text-[12px] text-gray-500 mt-0.5">
            <span className="min-w-0">
              {to.length > 0 && <span>To: {to.map(participantLabel).join(', ')}</span>}
              {cc.length > 0 && <span>{to.length > 0 ? ' · ' : ''}Cc: {cc.map(participantLabel).join(', ')}</span>}
            </span>
            {sourceChip}
          </div>
        </div>
      </div>

      <div className={`mt-2.5 text-[13px] leading-relaxed max-w-[68ch] ${automatic ? 'text-gray-400' : 'text-gray-200'}`}>
        {hasText && !showHtml && (
          <>
            <div className="whitespace-pre-line break-words">{visible}</div>
            {quoted && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowQuoted((v) => !v)}
                  aria-expanded={showQuoted}
                  data-testid="quoted-toggle"
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-white/[0.08] bg-white/[0.03] text-[11px] text-gray-500 hover:text-white hover:border-white/[0.18] transition-colors"
                >
                  <span aria-hidden="true" className="tracking-widest leading-none">···</span>
                  {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
                </button>
                {showQuoted && (
                  <div className="mt-2 border-l-2 border-white/10 text-gray-500 pl-3 whitespace-pre-line break-words" data-testid="quoted-text">
                    {quoted}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {!hasText && !hasHtml && <div className="text-gray-500 italic">{m.snippet || 'No message body.'}</div>}
        {hasHtml && (showHtml || !hasText) && (
          <iframe
            title={`Original HTML of message from ${fromName}`}
            sandbox=""
            srcDoc={wrapHtml(m.bodyHtml)}
            className="w-full min-h-[240px] rounded-md border border-white/[0.08] bg-white"
          />
        )}
        {hasHtml && hasText && (
          <button
            type="button"
            onClick={() => setShowHtml((v) => !v)}
            className="mt-2 text-[11px] text-gray-500 hover:text-white underline underline-offset-2"
          >
            {showHtml ? 'Show plain text' : 'Show original HTML'}
          </button>
        )}
      </div>

      {((m.attachments && m.attachments.length > 0) || alias) && (
        <footer className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
          {(m.attachments || []).map((a) => (
            <a
              key={a.id}
              href={`/api/admin/communications/attachments/${a.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-white/[0.08] text-gray-300 hover:text-white hover:border-white/[0.18] transition-colors"
            >
              <Paperclip className="w-3 h-3" aria-hidden="true" />
              <span className="truncate max-w-[220px]">{a.filename || 'Attachment'}</span>
              {a.sizeBytes ? <span className="text-gray-500">· {formatBytes(a.sizeBytes)}</span> : null}
            </a>
          ))}
          {alias && (
            <span>
              {inbound
                ? `Arrived in ${staffFirst ? `${staffFirst}'s` : 'the'} inbox (${localPart(alias.alias)} alias)`
                : `Sent from ${staffFirst ? `${staffFirst}'s` : 'the'} mailbox as ${localPart(alias.alias)}`}
            </span>
          )}
        </footer>
      )}
    </article>
  );
}

// Wrap untrusted HTML so it renders in the sandboxed iframe with readable
// defaults. sandbox="" blocks scripts, forms, and same-origin access.
export function wrapHtml(html) {
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{margin:12px;font:14px/1.5 -apple-system,system-ui,sans-serif;color:#111;background:#fff}img{max-width:100%}</style></head><body>${html}</body></html>`;
}
